import React, { useState } from 'react';
import { ExternalLink, X } from 'lucide-react';
import { Button } from './ui/button';
import { useContent } from '../context/ContentContext';
import { useLang } from '../context/LanguageContext';
import Reveal from './Reveal';

const FALLBACK = {
  section_title: 'Unsere Projekte',
  section_subtitle: 'Entdecken Sie unsere realisierten Lösungen',
  items: [],
};

const Projects = () => {
  const [selectedProject, setSelectedProject] = useState(null);
  const data = useContent('projects', FALLBACK);
  const { t } = useLang();

  return (
    <section id="projects" className="py-24 px-6 bg-white">
      <div className="max-w-[1200px] mx-auto">
        <Reveal className="text-center mb-20">
          <h2 className="text-4xl md:text-5xl font-semibold text-[#1D1D1F] mb-4 tracking-tight" data-testid="projects-title">
            {data.section_title}
          </h2>
          <p className="text-xl text-[#86868B] max-w-2xl mx-auto" data-testid="projects-subtitle">
            {data.section_subtitle}
          </p>
        </Reveal>

        <Reveal delay={100}>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {(data.items || []).map((project) => (
            <div
              key={project.id}
              className="group cursor-pointer"
              onClick={() => setSelectedProject(project)}
              data-testid={`project-card-${project.id}`}
            >
              <div className="relative h-72 overflow-hidden rounded-2xl bg-[#F5F5F7] mb-5">
                <img
                  src={project.image}
                  alt={project.title}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700 ease-out"
                  onError={(e) => {
                    e.target.src = 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=800&h=600&fit=crop';
                  }}
                />
              </div>

              <div>
                <div className="text-xs text-[#86868B] mb-2 uppercase tracking-wider">
                  {project.category}
                </div>
                <h3 className="text-2xl font-semibold text-[#1D1D1F] mb-2">{project.title}</h3>
                <p className="text-[#86868B] mb-3 line-clamp-2">{project.description}</p>
                <div className="text-[#0071E3] text-sm font-medium group-hover:underline">
                  {t('projects.more')}
                </div>
              </div>
            </div>
          ))}
        </div>
        </Reveal>
      </div>

      {selectedProject && (
        <div
          className="fixed inset-0 bg-[#1D1D1F]/40 backdrop-blur-md z-50 flex items-center justify-center p-6"
          onClick={() => setSelectedProject(null)}
        >
          <div
            className="bg-white rounded-3xl max-w-4xl w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="relative h-96 overflow-hidden rounded-t-3xl">
              <img
                src={selectedProject.image}
                alt={selectedProject.title}
                className="w-full h-full object-cover"
                onError={(e) => {
                  e.target.src = 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=800&h=600&fit=crop';
                }}
              />
              <button
                onClick={() => setSelectedProject(null)}
                className="absolute top-6 right-6 bg-white/90 backdrop-blur-xl hover:bg-white text-[#1D1D1F] w-10 h-10 rounded-full flex items-center justify-center transition-colors"
                data-testid="project-modal-close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-12">
              <div className="mb-4">
                <span className="text-xs text-[#86868B] uppercase tracking-wider">
                  {selectedProject.category} • {selectedProject.year}
                </span>
              </div>
              <h2 className="text-4xl font-semibold text-[#1D1D1F] mb-4">{selectedProject.title}</h2>
              <p className="text-[#86868B] text-lg mb-8 leading-relaxed">{selectedProject.description}</p>

              <div className="mb-8">
                <h3 className="text-xl font-semibold text-[#1D1D1F] mb-4">{t('projects.functions')}</h3>
                <div className="grid md:grid-cols-2 gap-3">
                  {(selectedProject.features || []).map((feature, index) => (
                    <div key={index} className="flex items-start space-x-3">
                      <div className="w-1 h-1 bg-[#1D1D1F] rounded-full mt-2.5 flex-shrink-0"></div>
                      <span className="text-[#86868B]">{feature}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="mb-8">
                <h3 className="text-xl font-semibold text-[#1D1D1F] mb-4">{t('projects.technologies')}</h3>
                <div className="flex flex-wrap gap-2">
                  {(selectedProject.technologies || []).map((tech, index) => (
                    <span key={index} className="bg-[#F5F5F7] text-[#1D1D1F] px-4 py-2 rounded-full text-sm">
                      {tech}
                    </span>
                  ))}
                </div>
              </div>

              {selectedProject.liveUrl && selectedProject.showLiveUrl !== false && (
                <Button
                  className="bg-[#0071E3] hover:bg-[#0077ED] text-white rounded-full w-full h-12"
                  onClick={() => window.open(selectedProject.liveUrl, '_blank')}
                  data-testid="project-live-demo"
                >
                  {t('projects.liveDemo')}
                  <ExternalLink className="ml-2 w-4 h-4" />
                </Button>
              )}
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default Projects;
